/**
 * Cartes cadeaux SKILLCARDS + achat par le parent pour son enfant
 * (migrations 0326-0327) — client des RPC + helpers PURS (testés).
 *
 * - Cadeau : on achète une offre « à offrir » (prix plein), le webhook crée un
 *   CODE (8 caractères) ; on le partage par code, QR, email ou partage natif ;
 *   le bénéficiaire l'active sur `/cadeau/{code}`.
 * - Parent : offres de l'enfant (`get_child_skillcard_offers`), achat au nom
 *   de l'enfant (`create-skillcard-checkout` + `for_student_id`).
 */

import { publishProgress } from '@/lib/progress-sync';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SkillcardOfferKind, SkillcardQuote } from '@jeraisonne/db/cards';
import { formatEuros } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

/** Origine FIXE des liens partagés (jamais `capacitor://` en natif). */
export const GIFT_PUBLIC_ORIGIN = 'https://app.jeraisonne.fr';

// Alphabet des codes : sans I, L, O, 0, 1 (cf. new_skillcard_gift_code).
const GIFT_CODE = /^[A-HJKMNP-Z2-9]{8}$/;

/** « abcd-efgh » / « ABCD EFGH » → « ABCDEFGH » ; null si invalide. */
export function normalizeGiftCode(input: string): string | null {
  const c = input.toUpperCase().replace(/[\s-]/g, '');
  return GIFT_CODE.test(c) ? c : null;
}

/** « ABCDEFGH » → « ABCD-EFGH » (lisible). */
export function formatGiftCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

export function giftUrl(code: string): string {
  return `${GIFT_PUBLIC_ORIGIN}/cadeau/${code}`;
}

export function giftLotLabel(kind: SkillcardOfferKind, label: string, count: number): string {
  const clean = label.replace(/\$/g, '');
  if (kind === 'card') return `la SKILLCARD « ${clean} »`;
  return `« ${clean} » (${count} SKILLCARD${count > 1 ? 'S' : ''})`;
}

/** Texte du partage / de l'email. */
export function giftShareText(gift: Pick<SkillcardGift, 'kind' | 'label' | 'card_count' | 'code'>) {
  return `Je t’offre ${giftLotLabel(gift.kind, gift.label, gift.card_count)} sur JeRaisonne ! Ton code : ${formatGiftCode(gift.code)}. Active-le ici :`;
}

/** Lien `mailto:` (ouvre la messagerie de l'acheteur, rien n'est envoyé par nous). */
export function giftMailto(gift: Pick<SkillcardGift, 'kind' | 'label' | 'card_count' | 'code'>) {
  const subject = 'Un cadeau pour toi sur JeRaisonne';
  const body = `${giftShareText(gift)} ${giftUrl(gift.code)}`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function giftRedeemErrorMessage(code: string): string {
  switch (code) {
    case 'not_found':
      return 'Ce code cadeau n’existe pas. Vérifie les 8 caractères.';
    case 'already_redeemed':
      return 'Ce cadeau a déjà été activé par quelqu’un d’autre.';
    case 'already_mine':
      return 'Tu as déjà activé ce cadeau : les cartes sont dans ton deck.';
    case 'account_required':
      return 'Connecte-toi avec ton compte pour activer ce cadeau.';
    default:
      return 'L’activation a échoué. Réessaie.';
  }
}

// ── Types ───────────────────────────────────────────────────────────────────

export interface SkillcardGift {
  id: string;
  purchase_id: string;
  code: string;
  kind: SkillcardOfferKind;
  label: string;
  card_count: number;
  first_card_id: string | null;
  redeemed_at: string | null;
  created_at: string;
}

export interface PublicGift {
  status: 'available' | 'redeemed' | 'not_found';
  mine?: boolean;
  kind?: SkillcardOfferKind;
  label?: string;
  card_count?: number;
  card_id?: string | null;
  from_first_name?: string | null;
}

export interface GiftOffers {
  card: SkillcardQuote;
  packs: SkillcardQuote[];
  programmes: SkillcardQuote[];
}

export type ChildPackOffer = SkillcardQuote & {
  pack_title: string;
  start_month: number;
  end_month: number | null;
  card_id: string | null;
};

export interface ChildProgrammeOffers {
  subject: string;
  programme_id: string;
  programme: SkillcardQuote | null;
  programme_card_id: string | null;
  packs: ChildPackOffer[];
}

export interface ChildOffers {
  student: { id: string; first_name: string | null; level: string | null };
  premium: boolean;
  offers: ChildProgrammeOffers[];
}

/** Prix d'une offre pour un message (« 8,60 € »). */
export function offerPriceLabel(q: Pick<SkillcardQuote, 'price_cents'>): string {
  return formatEuros(q.price_cents);
}

// ── RPC / requêtes ──────────────────────────────────────────────────────────

export function useGiftOffers(cardId: string, enabled = true) {
  return useQuery({
    queryKey: ['skillcard-gift-offers', cardId],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<GiftOffers | null> => {
      const { data, error } = await db.rpc('get_skillcard_gift_offers', { p_card_id: cardId });
      if (error) throw new Error(error.message);
      return (data as GiftOffers | null) ?? null;
    },
  });
}

export const myGiftsKey = (userId: string) => ['skillcard-gifts', userId] as const;

/** Cadeaux achetés par l'utilisateur (RLS own_select). */
export function useMyGifts(userId: string | null | undefined, pollWhilePending = false) {
  return useQuery({
    queryKey: myGiftsKey(userId ?? ''),
    enabled: !!userId,
    refetchInterval: pollWhilePending ? 2000 : false,
    queryFn: async (): Promise<SkillcardGift[]> => {
      const { data, error } = await db
        .from('skillcard_gifts')
        .select(
          'id, purchase_id, code, kind, label, card_count, first_card_id, redeemed_at, created_at',
        )
        .eq('buyer_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as SkillcardGift[];
    },
  });
}

export function usePublicGift(code: string | null) {
  return useQuery({
    queryKey: ['skillcard-gift', code],
    enabled: !!code,
    queryFn: async (): Promise<PublicGift> => {
      const { data, error } = await db.rpc('get_skillcard_gift', { p_code: code });
      if (error) throw new Error(error.message);
      return data as PublicGift;
    },
  });
}

export async function redeemGift(code: string): Promise<{
  ok: true;
  card_id: string | null;
  kind: SkillcardOfferKind;
  label: string;
}> {
  const { data, error } = await db.rpc('redeem_skillcard_gift', { p_code: code });
  if (error) throw new Error('network');
  const r = data as {
    ok?: boolean;
    error?: string;
    card_id?: string | null;
    kind?: SkillcardOfferKind;
    label?: string;
  };
  if (!r?.ok) throw new Error(r?.error ?? 'unknown');
  publishProgress('cards-granted');
  return { ok: true, card_id: r.card_id ?? null, kind: r.kind ?? 'card', label: r.label ?? '' };
}

export function useChildSkillcardOffers(studentId: string | null) {
  return useQuery({
    queryKey: ['child-skillcard-offers', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async (): Promise<ChildOffers | null> => {
      const { data, error } = await db.rpc('get_child_skillcard_offers', {
        p_student_id: studentId,
      });
      if (error) throw new Error(error.message);
      const r = data as ChildOffers & { error?: string };
      return r?.error ? null : r;
    },
  });
}
